import {
  CancelablePromise,
  FanResponseDto,
  TranslationServiceService,
} from '@api'
import { attach, combine, createEvent, createStore, sample } from 'effector'
import { useUnit } from 'effector-react'
import { createEffect } from 'effector/compat'

import { $activeChatDetails, $fanId } from '@/models/chattingUI/chats/chats.ts'
import { $creatorId } from '@/models/creators.ts'
import type { AITranslationLanguageType } from '@/modules/chatting/ui/chat-window/translation/types.ts'
import { handleError } from '@/utils/error'

import { $messagesByFanIdMap } from './messages'


export const setChatterTranslationConfig = createEvent<{
  tutorialViewed?: boolean
  ownLanguage?: AITranslationLanguageType
}>("set chatter's translation config")
export const $chatterTranslationConfig = createStore<{
  tutorialViewed?: boolean
  ownLanguage?: AITranslationLanguageType
}>(
  {},
  {
    name: "chatter's translation config",
  },
).on(setChatterTranslationConfig, (_, value) => value)

export const setFanTranslationConfigs = createEvent<
  Record<
    FanResponseDto['id'],
    {
      autoTranslationEnabled?: boolean
      translatorEnabled?: boolean
    }
  >
>("set chatter's translation config")
export const $fanTranslationConfigs = createStore<
  Record<
    FanResponseDto['id'],
    {
      autoTranslationEnabled?: boolean
      translatorEnabled?: boolean
    }
  >
>(
  {},
  {
    name: "fan's translation configs",
  },
).on(setFanTranslationConfigs, (_, config) => config)

export const setAITranslatorEnabled = createEvent<boolean>(
  "set AI translator's enabled",
)

export const $isAITranslatorEnabled = createStore<boolean>(false, {
  name: 'is AI translator enabled',
}).on(setAITranslatorEnabled, (_, value) => value)

export const $isAutoTranslationsEnabled = combine(
  $fanId,
  $fanTranslationConfigs,
  (fanId, configs) => configs[fanId]?.autoTranslationEnabled ?? false,
)

export const $isTranslatorEnabled = combine(
  $isAITranslatorEnabled,
  $fanId,
  $fanTranslationConfigs,
  (isAITranslatorEnabled, fanId, configs) =>
    isAITranslatorEnabled && (configs[fanId]?.translatorEnabled ?? true),
)

export const $fanLanguage = combine(
  $isTranslatorEnabled,
  $activeChatDetails,
  (isTranslationsEnabled, chat) =>
    isTranslationsEnabled && chat?.withUser.translationLanguage
      ? (chat.withUser.translationLanguage as AITranslationLanguageType)
      : null,
)

export const $chatterLanguage = combine(
  $chatterTranslationConfig,
  ({ ownLanguage }) => ownLanguage ?? null,
)

export const translateMessagesFx = attach({
  name: 'translate messages',
  source: {
    userId: $fanId,
    language: $chatterLanguage,
    creatorId: $creatorId,
  },
  mapParams: (
    { messageIds }: { messageIds: number[]; forceShow?: boolean },
    { creatorId, userId, language },
  ) => ({
    creatorId: creatorId!,
    language: language!,
    userId: userId!,
    requestBody: messageIds,
  }),
  effect: createEffect(TranslationServiceService.translateMessage),
})

translateMessagesFx.failData.watch((error) => {
  handleError(error, 'Something went wrong while translating')
})

export const addMessageIdsToTranslate = createEvent<number[]>(
  'add message ids to translate',
)
export const $messageIdsToTranslate = createStore<number[]>([], {
  name: "messages' ids to translate",
})
  .on(addMessageIdsToTranslate, (store, ids) => store.concat(ids))
  .on(translateMessagesFx.finally, (store, { params: { messageIds } }) =>
    store.filter((id) => !messageIds.includes(id)),
  )

export const toggleTranslation = createEvent<number>(
  'toggle message translation',
)

export const hideTranslations = createEvent<number[]>('hide translations')

let translateTextsRequest: CancelablePromise<Array<string>> | null = null

export const translateTextsFx = attach({
  name: 'translate text',
  source: {
    creatorId: $creatorId,
  },
  mapParams: (
    { language, texts }: { texts: string[]; language: string },
    { creatorId },
  ) => {
    return {
      creatorId: creatorId!,
      language,
      requestBody: texts,
      initiationCause: 'Chat',
    }
  },
  effect: createEffect(
    (...params: Parameters<typeof TranslationServiceService.translate>) => {
      translateTextsRequest = TranslationServiceService.translate(...params)
      translateTextsRequest.then(() => {
        translateTextsRequest = null
      })

      return translateTextsRequest
    },
  ),
})

translateTextsFx.failData.watch((error) => {
  handleError(error, 'Something went wrong while translating')
})

export const cancelTranslateTextsRequest = () => {
  if (!translateTextsRequest) return

  translateTextsRequest.cancel()
}

interface MessageTranslation {
  text: string | null
  isLoading: boolean
  isError: boolean
  forceShow?: boolean
}
export const $translationsByMessageIdMap = createStore<
  Record<number, Partial<Record<AITranslationLanguageType, MessageTranslation>>>
>({}, { name: 'translations map by message id' })
  .on(addMessageIdsToTranslate, (store, messageIds) => {
    // eslint-disable-next-line effector/no-getState
    const language = $chatterLanguage.getState()!

    return messageIds.reduce<
      Record<
        number,
        Partial<Record<AITranslationLanguageType, MessageTranslation>>
      >
    >(
      (result, id) => {
        if (result[id]?.[language]) {
          result[id] = {
            ...result[id],
            [language]: {
              ...result[id][language],
              isLoading: true,
            },
          }
        } else {
          result[id] = {
            ...result[id],
            [language]: {
              text: null,
              isLoading: true,
              isError: false,
            },
          }
        }

        return result
      },
      { ...store },
    )
  })
  .on(translateMessagesFx, (store, { messageIds, forceShow }) => {
    // eslint-disable-next-line effector/no-getState
    const language = $chatterLanguage.getState()!

    return messageIds.reduce(
      (result, id) => {
        result[id] = {
          ...result[id],
          [language]: {
            text: null,
            isLoading: true,
            isError: false,
            forceShow,
          },
        }

        return result
      },
      { ...store },
    )
  })
  .on(
    translateMessagesFx.done,
    (store, { params: { forceShow, messageIds }, result: translations }) => {
      // eslint-disable-next-line effector/no-getState
      const language = $chatterLanguage.getState()!

      return messageIds.reduce(
        (result, id, index) => {
          result[id] = {
            ...result[id],
            [language]: {
              text: translations[index]!,
              isLoading: false,
              isError: false,
              forceShow,
            },
          }

          return result
        },
        { ...store },
      )
    },
  )
  .on(
    translateMessagesFx.fail,
    (store, { params: { messageIds, forceShow } }) => {
      // eslint-disable-next-line effector/no-getState
      const language = $chatterLanguage.getState()!

      return messageIds.reduce(
        (result, id) => {
          result[id] = {
            ...result[id],
            [language]: {
              text: null,
              isLoading: false,
              isError: true,
              forceShow,
            },
          }

          return result
        },
        { ...store },
      )
    },
  )
  .on(toggleTranslation, (store, messageId) => {
    // eslint-disable-next-line effector/no-getState
    const language = $chatterLanguage.getState()!
    const translation = store[messageId]?.[language]

    if (translation?.text) {
      return {
        ...store,
        [messageId]: {
          ...store[messageId],
          [language]: {
            ...store[messageId][language],
            forceShow: !translation.forceShow,
          },
        },
      }
    }

    return store
  })
  .on(hideTranslations, (store, messageIds) => {
    // eslint-disable-next-line effector/no-getState
    const language = $chatterLanguage.getState()!

    return messageIds.reduce(
      (result, id) => {
        if (result[id]) {
          result[id] = {
            ...result[id],
            [language]: {
              ...result[id][language],
              forceShow: false,
            },
          }
        }

        return result
      },
      { ...store },
    )
  })

export const useMessageTranslation = (messageId: number) => {
  const translationsByMessageIdMap = useUnit($translationsByMessageIdMap)
  const chatterLanguage = useUnit($chatterLanguage)
  const isAutoTranslationsEnabled = useUnit($isAutoTranslationsEnabled)
  const isAITranslatorEnabled = useUnit($isTranslatorEnabled)

  if (!chatterLanguage) {
    return null
  }

  const { isLoading, isError, text, forceShow } =
    translationsByMessageIdMap[messageId]?.[chatterLanguage] ?? {}

  if (!isAITranslatorEnabled || (!isAutoTranslationsEnabled && !forceShow)) {
    return null
  }

  return {
    isLoading,
    isError,
    text,
  }
}

sample({
  clock: [$isAutoTranslationsEnabled, $isTranslatorEnabled, $chatterLanguage],
  source: {
    messagesByFanIdMap: $messagesByFanIdMap,
    translationsByMessageIdMap: $translationsByMessageIdMap,
    fanId: $fanId,
    chatterLanguage: $chatterLanguage,
    isAutoTranslationsEnabled: $isAutoTranslationsEnabled,
    isTranslatorEnabled: $isTranslatorEnabled,
  },
  filter: ({
    fanId,
    isAutoTranslationsEnabled,
    isTranslatorEnabled,
    chatterLanguage,
    messagesByFanIdMap,
  }) =>
    !!fanId &&
    !!chatterLanguage &&
    (!isTranslatorEnabled || !isAutoTranslationsEnabled) &&
    !!messagesByFanIdMap[fanId],
  fn: ({
    messagesByFanIdMap,
    translationsByMessageIdMap,
    fanId,
    chatterLanguage,
  }) =>
    messagesByFanIdMap[fanId]
      .filter(
        (message) =>
          translationsByMessageIdMap?.[message.id]?.[chatterLanguage!]
            ?.forceShow,
      )
      .map(({ id }) => id),
  target: hideTranslations,
})

sample({
  clock: toggleTranslation,
  source: {
    messagesMap: $translationsByMessageIdMap,
    language: $chatterLanguage,
  },
  filter: ({ messagesMap, language }, messageId) =>
    !!language && !messagesMap[messageId]?.[language]?.text,
  fn: (_, messageId) => ({ forceShow: true, messageIds: [messageId] }),
  target: translateMessagesFx,
})
